/**
 * /settings/members — workspace member directory.
 *
 * Phase 5 surface: lists every active member of the active workspace
 * with role and join date. Invitations (Brevo magic-link signup) land
 * in Phase 6+; this page focuses on visibility so founders can sanity-
 * check membership today.
 */
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { workspaceMemberKeys, workspaceMembersApi } from '../../../lib/api/invitations';
import { useAuthStore } from '../../../stores/auth-store';

function MembersPage() {
  const { t } = useTranslation();
  const workspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const membersQ = useQuery({
    queryKey: workspaceId ? workspaceMemberKeys.list(workspaceId) : ['noop'],
    queryFn: () => (workspaceId ? workspaceMembersApi.list(workspaceId) : Promise.resolve([])),
    enabled: !!workspaceId,
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="px-6 pt-3 pb-2">
        <h1 className="text-[15px] font-semibold tracking-tight text-foreground">
          {t('settings.members.title')}
        </h1>
        <p className="mt-1 text-[12px] text-muted-foreground">{t('settings.members.subtitle')}</p>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
        <table className="w-full max-w-3xl text-left text-[13px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-muted-foreground/80">
              <th className="border-b border-border px-2 py-2 font-medium">
                {t('settings.members.name_header')}
              </th>
              <th className="border-b border-border px-2 py-2 font-medium">
                {t('settings.members.email_header')}
              </th>
              <th className="border-b border-border px-2 py-2 font-medium">
                {t('settings.members.role_header')}
              </th>
              <th className="border-b border-border px-2 py-2 font-medium">
                {t('settings.members.joined_header')}
              </th>
            </tr>
          </thead>
          <tbody>
            {membersQ.isLoading && (
              <tr>
                <td colSpan={4} className="px-2 py-3 text-muted-foreground">
                  {t('common.loading')}
                </td>
              </tr>
            )}
            {membersQ.data?.map((m) => (
              <tr key={m.userId} className="border-b border-border/60 last:border-b-0">
                <td className="px-2 py-2 font-medium text-foreground">
                  {m.displayName ?? m.email}
                </td>
                <td className="px-2 py-2 text-muted-foreground">{m.email}</td>
                <td className="px-2 py-2">
                  <span className="inline-flex items-center rounded bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-foreground">
                    {m.role}
                  </span>
                </td>
                <td className="px-2 py-2 text-muted-foreground">
                  {new Date(m.joinedAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/settings/members')({
  component: MembersPage,
});
